import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { BlockElementTypes, BlockTypes } from '@pm2/course-builder';
import { useCourseRevisionState, useCanDisableChapterInRevision, courseRevisionsStore } from '@pm2/course-revisions';
import { pm2SitemapStore, findCourseDescriptorFromParents, getNestedPages } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { AddToCourseBuilder } from '@pm2/ui-components';
import { useLocation, Link } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { Tooltip, Switch, ToggledIcon } from '@ui/kit';
import { useLoaderState, useKatex, SizeTransition, Chevron } from '@utils/react';
import { itemContentCls, switchTransitionCls, switchCls, linkChevron, revisionIconWrapper, linkCls, expandButtonCls, chevronCls, throbberCls, throbberWrapperCls, navItemCls, addToBasketCls } from './nav-item.style.js';
import { NavLayer } from './nav-layer.js';
import { courseRevisionEditTransitionDurationMs, NAV_LAYER_COLLAPSE_EXPAND_DURATION_MS, transitionCls, navItemTransitionCls } from './nav-layer.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function NavItem({
  pageDescriptor,
  onLinkClick,
  ...props
}) {
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;
  const isEditing = useCourseRevisionState(state => state.isEditing);
  const isEnabledInRevision = useCourseRevisionState(state => !state.disabledIds[pageDescriptor.id]?.disabled);
  const hasRevisedElementsWithin = useCourseRevisionState(state => Object.entries(state.disabledIds[pageDescriptor.id] ?? {}).some(([key, value]) => key !== "disabled" && value));
  const location = useLocation();
  const user = useUserIdentityState(state => state.user);
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const showCoursePlanner = pm2ConfigStore.useSelector(state => state.config.settings.showCoursePlanner);
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const isCurrentPath = pageDescriptor.path === location.pathname;
  const courseDescriptor = findCourseDescriptorFromParents(sitemap, pageDescriptor);
  const isWithinCourse = !!courseDescriptor && pageDescriptor.id !== courseDescriptor.id;
  const [isLoading, setLoading] = useState(false);
  const [isExpanded, setExpanded] = useState(shouldAutoExpand(location.pathname, pageDescriptor));
  const showLoader = useLoaderState(isLoading);
  const canDisableChapterInRevision = useCanDisableChapterInRevision(courseDescriptor, pageDescriptor);
  useKatex(ref);
  useEffect(() => {
    if (shouldAutoExpand(location.pathname, pageDescriptor)) {
      // auto-expand this item when the page is activated
      setExpanded(true);
    }
  },
  // intentionally leave out pageDescriptor - we're only reacting to
  // navigation events here...
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [location.pathname]);
  const handleChevronClick = evt => {
    evt.stopPropagation();
    evt.preventDefault();
    if (isLoading) {
      // bail out if we're waiting for resources to become available
      return;
    }
    if (isExpanded) {
      setExpanded(false);
    } else {
      setLoading(true);
      Promise.resolve(getNestedPages(configStore, sitemapStore, pageDescriptor)).then(async () => {
        setLoading(false);
        while (showLoader.current) {
          // don't expand while a loader is being displayed
          await new Promise(resolve => setTimeout(resolve, 1));
        }
        setExpanded(true);
      }).catch(() => {
        alert("Der opstod en fejl ved indlæsning af menuen, prøv igen.");
      });
    }
  };
  const handleRevisionToggle = async () => {
    courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleChapter({
      disabled: isEnabledInRevision,
      chapterId: pageDescriptor.id
    }));
  };
  return /*#__PURE__*/jsxs("li", {
    ref: ref,
    className: cx(navItemCls, navItemTransitionCls),
    children: [showCoursePlanner && renderCourseBuilderAddButton(user, pageDescriptor), /*#__PURE__*/jsxs("div", {
      className: itemContentCls,
      children: [/*#__PURE__*/jsx(TransitionGroup, {
        appear: true,
        children: isEditing && isWithinCourse && /*#__PURE__*/jsx(SizeTransition, {
          axis: "horisontal",
          timeout: courseRevisionEditTransitionDurationMs,
          classNames: switchTransitionCls,
          skipValidation: true,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: switchTransitionCls,
            children: /*#__PURE__*/jsx(Tooltip, {
              body: "Du skal have mindst \xE9t kapitel tilg\xE6ngeligt i forl\xF8bet",
              disabled: canDisableChapterInRevision || !isEnabledInRevision,
              placement: "top",
              ariaType: "description",
              children: ref => /*#__PURE__*/jsx("div", {
                ref: ref,
                className: switchCls,
                children: /*#__PURE__*/jsx(Switch, {
                  checked: isEnabledInRevision,
                  disabled: !canDisableChapterInRevision && isEnabledInRevision,
                  "aria-label": isEnabledInRevision ? `Fjern "${pageDescriptor.title}" fra din revision` : `Tilføj "${pageDescriptor.title}" til din revision`,
                  onChange: handleRevisionToggle
                })
              })
            })
          })
        })
      }), /*#__PURE__*/jsxs(Link, {
        href: pageDescriptor.path,
        className: cx(linkCls, isCurrentPath && "isCurrent"),
        "aria-current": isCurrentPath ? "page" : undefined,
        onClick: onLinkClick,
        children: [/*#__PURE__*/jsx(Chevron, {
          className: linkChevron,
          size: 6,
          lineWidth: 1.5
        }), pageDescriptor.title, /*#__PURE__*/jsx(TransitionGroup, {
          appear: true,
          component: null,
          children: isEditing && /*#__PURE__*/jsx(SizeTransition, {
            axis: "horisontal",
            timeout: courseRevisionEditTransitionDurationMs,
            classNames: revisionIconWrapper,
            skipValidation: true,
            children: ref => /*#__PURE__*/jsx("span", {
              ref: ref,
              className: cx(revisionIconWrapper, hasRevisedElementsWithin && "visible"),
              children: /*#__PURE__*/jsx(ToggledIcon, {})
            })
          })
        })]
      }), pageDescriptor.hasChildren && /*#__PURE__*/jsx(Tooltip, {
        ariaType: "label",
        placement: "top",
        body: isExpanded ? "Skjul undermenupunkter" : "Vis undermenupunkter",
        children: ref => /*#__PURE__*/jsxs("button", {
          ref: ref,
          onClick: handleChevronClick,
          className: expandButtonCls,
          children: [/*#__PURE__*/jsx("span", {
            className: cx(chevronCls, showLoader.current && "hidden"),
            children: /*#__PURE__*/jsx(Chevron, {
              vertical: true,
              inversed: isExpanded
            })
          }), /*#__PURE__*/jsx("span", {
            className: cx(throbberWrapperCls, !showLoader.current && "hidden"),
            children: /*#__PURE__*/jsx("span", {
              className: throbberCls
            })
          })]
        })
      })]
    }), /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      in: isExpanded,
      classNames: transitionCls,
      timeout: NAV_LAYER_COLLAPSE_EXPAND_DURATION_MS,
      unmountOnExit: true,
      mountOnEnter: true,
      children: ref => /*#__PURE__*/jsx(NavLayer, {
        ref: ref,
        parentDescriptor: pageDescriptor,
        onLinkClick: onLinkClick
      })
    })]
  });
}

/**
 * auto-xepand items if they're currently active (ie. if the user has navigated
 * to this item of another item inside it) and they contain children.
 */
function shouldAutoExpand(path, pageDescriptor) {
  return path.indexOf(pageDescriptor.path) === 0 && !!pageDescriptor.children?.length;
}

/**
 * render the add to course builder basket utility if it's enabled on the
 * portal and the user is currently signed in as a super user.
 */
function renderCourseBuilderAddButton(user, pageDescriptor) {
  // ... and only for users who are teachers (we can safely assume that the user
  // has been resolved by the time we're parsing page content, as that feed
  // cannot be loaded until authentication has completed)
  if (user?.role !== "teacher") {
    return null;
  }
  if (pageDescriptor.type !== "Chapter") {
    // Bail out if the current page isn't a chapter, which is the only supported
    return null;
  }
  return /*#__PURE__*/jsx(AddToCourseBuilder, {
    block: {
      type: BlockTypes.REFERENCE,
      element: {
        id: pageDescriptor.id,
        title: pageDescriptor.title,
        type: BlockElementTypes.PAGE
      }
    },
    className: addToBasketCls,
    small: true,
    withoutOverlay: true
  });
}

export { NavItem };
//# sourceMappingURL=nav-item.js.map
